import { JSONContent } from '@tiptap/core';
import type { TextBoxStyle } from 'PDF/redux/PDFAnnotationsSlice';

export const isRichTextEmpty = (content?: RichTextJSON) =>
  !content?.content || !content.content[0] || !('content' in content.content[0]);

export const containsMention = (doc?: RichTextJSON) =>
  doc?.content &&
  doc.content?.some((paragraph) => paragraph.content?.some((node) => node.type === 'mention'));

export const richTextToPlainText = (content?: RichTextJSON) => {
  if (!content?.content) {
    return '';
  }

  return content.content
    .map((paragraph) =>
      paragraph.content
        ?.map((node) => {
          switch (node.type) {
            case 'text':
              return node.text;
            case 'mention':
              return `@${node.label}`;
            default:
              return '';
          }
        })
        .join(''),
    )
    .join(' ');
};

export const createRichText = ({
  text,
  styles,
  mention,
}: {
  /**
   * Text cannot be empty, TipTap (ProseMirror) does not support empty paragraphs
   */
  text: string;
  styles?: TextBoxStyle;
  mention?: { id: UserId; label: string; placement: 'start' | 'end' };
}): RichTextJSON => {
  const paragraphContent: NonNullable<RichTextJSON['content']>[number] = {
    type: 'text',
    marks: [],
    text,
  };

  if (styles) {
    const { textStyles, marks } = Object.typedKeys(styles).reduce<{
      textStyles: Partial<Pick<TextBoxStyle, 'fontFamily' | 'fontSize' | 'color'>>;
      marks: NonNullable<JSONContent['marks']>;
    }>(
      ({ textStyles, marks }, style) => {
        if (style === 'fontSize') {
          textStyles.fontSize = styles.fontSize;
        } else if (style === 'fontFamily') {
          textStyles.fontFamily = styles.fontFamily;
        } else if (style === 'color') {
          textStyles.color = styles.color;
        } else if (style === 'bold' && styles.bold) {
          marks.push({
            type: 'bold',
          });
        } else if (style === 'italic' && styles.italic) {
          marks.push({
            type: 'italic',
          });
        } else if (style === 'underline' && styles.underline) {
          marks.push({
            type: 'underline',
          });
        }

        return {
          textStyles,
          marks,
        };
      },
      { textStyles: {}, marks: [] },
    );

    if (Object.keys(textStyles).length) {
      paragraphContent.marks?.push({
        type: 'textStyle',
        attrs: {
          ...textStyles,
          fontSize: `${textStyles.fontSize}pt`,
        },
      });
    }

    if (marks.length) {
      paragraphContent.marks?.push(...marks);
    }
  }

  const paragraph: NonNullable<RichTextJSON['content']> = [paragraphContent];

  if (mention) {
    const mentionMark: NonNullable<JSONContent['marks']>[number] = {
      type: 'mention',
      attrs: mention,
    };

    switch (mention.placement) {
      case 'start':
        paragraph.unshift(mentionMark);
        break;
      case 'end':
        paragraph.push(mentionMark);
    }
  }

  const children: RichTextJSON = {
    type: 'doc',
    content: [
      {
        type: 'paragraph',
        content: paragraph,
        attrs: { textAlign: styles?.align },
      },
    ],
  };

  return children;
};

export const parseFontSize = (value: number | string | undefined): number | undefined => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }

  if (typeof value !== 'string') {
    return undefined;
  }

  const normalized = value.trim();
  if (!normalized) return undefined;

  const numericValue = normalized.replace(/(pt|px)$/i, '');
  const parsed = Number.parseFloat(numericValue);

  return Number.isFinite(parsed) ? parsed : undefined;
};
